import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { GuidebookService, GuidebookConfig } from 'src/shared/services/guidebook.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'guidebook-preview',
  templateUrl: './guidebook-preview.component.html',
  styleUrls: ['./guidebook-preview.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class GuidebookPreviewComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private sanitizer = inject(DomSanitizer);
  private guidebookService = inject(GuidebookService);

  readonly windowService = inject(WindowService);

  guidebookConfig: GuidebookConfig;
  isDataLoaded = false;
  isRightSideContent = false;
  errorMessage: string;
  guidebookUrl = '';
  iframeUrl: SafeResourceUrl;

  async ngOnInit() {
    try {
      this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
      const params = await this.storage.retrieve('dialog-params');
      const guidebookConfigId = params?.guidebookConfigId;

      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogPageTitle('Guidebook Preview');
      }

      if (guidebookConfigId) {
        await this.loadPage(guidebookConfigId);
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load guidebook preview';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage(guidebookConfigId: number) {
    try {
      this.guidebookConfig = await this.guidebookService.getGuidebookConfig(guidebookConfigId);
    } catch {
      this.errorMessage = 'Failed to load guidebook configuration';
      this.isDataLoaded = true;
      this.cdr.markForCheck();
      return;
    }

    // Use test token for now. Replace with real preview token when backend is ready.
    const baseUrl = this.guidebookConfig.guidebook_base_url || 'http://localhost:3000';
    const previewUrl = `${baseUrl}/test-guidebook`;
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
    this.guidebookUrl = previewUrl;

    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('guidebook');
    }
  }
}
